import { useState, useEffect } from 'react';
import { Organization } from '@/types/models';
import { useOrganization } from './useOrganization';
import { useUsers } from './useUsers';

interface OnboardingState {
  linkedinUrl: string;
  companyDetails: {
    companyName: string;
    companySize: string;
    industry: string;
    industry_other: string;
    website: string;
  };
  userDetails: {
    role: string;
    role_other: string;
    businessAreas: string[];
  };
  currentStep: number;
}

export function useOnboardingState() {
  const { organization, isLoading } = useOrganization();
  const { user } = useUsers();
  const [state, setState] = useState<OnboardingState>({
    linkedinUrl: '',
    companyDetails: {
      companyName: '',
      companySize: '',
      industry: '',
      industry_other: '',
      website: '',
    },
    userDetails: {
      role: '',
      role_other: '',
      businessAreas: [],
    },
    currentStep: 0
  });

  // Load initial data from organization and user when available
  useEffect(() => {
    if (organization && user) {
      setState(prev => ({
        ...prev,
        linkedinUrl: organization.linkedin_profile_url || '',
        companyDetails: {
          companyName: organization.linkedin_company_profile?.name || '',
          companySize: organization.linkedin_company_profile?.size || '',
          industry: organization.linkedin_company_profile?.industry || '',
          industry_other: '',
          website: organization.linkedin_company_profile?.company_website || '',
        },
        userDetails: {
          role: user.role || '',
          role_other: '',
          businessAreas: organization.business_areas || [],
        }
      }));
    }
  }, [organization, user]);

  // Save state to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('onboardingState', JSON.stringify(state));
  }, [state]);

  // Load state from localStorage on mount
  useEffect(() => {
    const savedState = localStorage.getItem('onboardingState');
    if (savedState) {
      setState(JSON.parse(savedState));
    }
  }, []);

  const updateState = (updates: Partial<OnboardingState>) => {
    setState(prev => ({ ...prev, ...updates }));
  };

  const clearState = () => {
    localStorage.removeItem('onboardingState');
    setState({
      linkedinUrl: '',
      companyDetails: {
        companyName: '',
        companySize: '',
        industry: '',
        industry_other: '',
        website: '',
      },
      userDetails: {
        role: '',
        role_other: '',
        businessAreas: [],
      },
      currentStep: 0
    });
  };

  return {
    state,
    updateState,
    clearState,
    isLoading
  };
} 